/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { Link } from "react-router-dom";

import { getTheme } from "office-ui-fabric-react";
import styled from "styled-components";

import { Link as AnchorLink } from "@fluentui/react/lib/Link";

const theme = getTheme();

/**
 * Icon image styles.
 */
export const Image = styled.img<{ active: boolean; $isActiveTenant: boolean }>`
  margin-right: 8px;
  padding-left: 14px;
  padding-left: ${(props) => (props.active || props.$isActiveTenant ? "12px" : "14px")};
`;

/**
 * Styles for the tooltip that appears
 * on hover of the collapsed menu items.
 * NOTE: THESE MUST STAY ABOVE LINKWRAPPER STYLES
 * FOR THE HOVER STYLES ON LINE 51 TO WORK.
 */
export const Tooltip = styled.div`
  background-color: ${theme.palette.neutralDark};
  border-radius: 2px;
  color: ${theme.palette.white};
  left: 50%;
  opacity: 0.85;
  padding: 4px 8px;
  position: absolute;
  top: 50%;
  visibility: hidden;
  z-index: 10;
`;

/**
 * Styles for the overall link
 * container (wrapper).
 */
export const LinkWrapper = styled(Link)<{
  $isActive?: boolean;
  $isActiveTenant?: boolean;
  $hasActiveChild?: boolean;
}>`
  border-left: ${(props) =>
    props.$isActive
      ? `2px solid ${theme.palette.themePrimary}`
      : props.$isActiveTenant && "2px solid #C4C4C4"};
  background-color: ${(props) =>
    props.$isActive || props.$hasActiveChild ? "#CDCDCD" : props.$isActiveTenant && "#D9D9D9"};
  padding: 5px 0;
  position: relative;
`;

/**
 * Styles for the anchor link
 * container (wrapper).
 */
export const AnchorWrapper = styled(AnchorLink)<{
  $active?: boolean;
  $activeTenant?: boolean;
}>`
  border-left: ${(props) =>
    props.$active
      ? `2px solid ${theme.palette.themePrimary}`
      : props.$activeTenant && "2px solid #C4C4C4"};
  background-color: ${(props) => (props.$active ? "#CDCDCD" : props.$activeTenant && "#D9D9D9")};
  padding: 5px 0;
  position: relative;
`;

/**
 * Styles for individual navigation links.
 */
export const NavLink = styled.p<{ active?: boolean; header?: boolean }>`
  color: #333333;
  display: inline-block;
  font-size: 14px;
  font-weight: ${(props) => (props.header ? "600" : props.active && "600")};
  line-height: 20px;
  margin: 0;
  padding: 5px 0;
  text-decoration: none;
  -webkit-transition: margin-left 0.2s ease-in-out;
  -moz-transition: margin-left 0.2s ease-in-out;
  -o-transition: margin-left 0.2s ease-in-out;
  transition: margin-left 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

/**
 * Styles for individual anchor links.
 */
export const AnchorLinkWrapper = styled.p<{
  active?: boolean;
  header?: boolean;
  menuOpen?: boolean;
}>`
  color: #333333;
  display: inline-block;
  font-size: 14px;
  font-weight: ${(props) => (props.header ? "600" : props.active && "600")};
  line-height: 20px;
  margin: 0;
  padding: 5px 0;
  text-decoration: none;
  -webkit-transition: margin-left 0.2s ease-in-out;
  -moz-transition: margin-left 0.2s ease-in-out;
  -o-transition: margin-left 0.2s ease-in-out;
  transition: margin-left 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

export const SubLinkWrapper = styled.div<{ $isActiveTenant: boolean; $hasActiveParent: boolean }>`
  display: flex;
  flex-direction: column;

  ${(props) =>
    props.$isActiveTenant &&
    props.$hasActiveParent &&
    `> a {
    background-color: #cdcdcd;
  }`}

  > a > img {
    margin-left: 10px;
    margin-right: 5px;
    height: 16px;
  }

  > a:not(:has(img)) > :first-child {
    margin-left: 25px;
  }
`;
